/**
 * Firefox bug that only shows up under very specific conditions where Firefox
 * is running on Linux, with hardware video decoding enabled (VA-API or V4L2),
 * and using modern Mesa drivers with DMA-BUF support, and when using our
 * pattern of closing the video frame shortly after initiating GL upload.
 *
 * Agent sniffing like this is generally discouraged, but we've been unable to
 * find a more reliable feature detection mechanism for this specific issue.
 *
 *
 * The basic flow that goes wrong is:
 *
 * - gl.texImage2D(videoFrame) calls FromVideoFrame()
 * - This calls videoFrame->GetImage() which returns the DMABUFSurfaceImage
 * - Firefox stores a pointer to this image for deferred upload
 * - Returns control to JavaScript
 * - Our TypeScript code calls videoFrame.close()
 * - This sets mResource to Nothing()
 * - But the DMABUFSurfaceImage destructor runs immediately!
 * - GlobalRefRelease() is called → FFmpeg thinks the surface is free
 * - FFmpeg immediately reuses the surface for the next decoded frame
 * - Later, when GLBlitHelper::BlitImage() tries to upload:
 *   - The DMA-BUF surface now contains a different frame.
 *   - You get frames out of order
 */
export function needPrematureVideoFrameReuseBugWorkaroundFromBrowserContext(): boolean {
  const ua = navigator.userAgent;
  const isFirefox = ua.includes("Firefox/");
  const isLinux = /\bLinux\b/i.test(ua) && !/\bAndroid\b/i.test(ua);
  return isFirefox && isLinux;
}

/**
 *  The possible values for this type field was based on:
 *    https://developer.mozilla.org/en-US/docs/Web/API/AudioSession
 *
 *  In practice only 'playback' has been tested.
 */
export type SafariAudioSessionType = 'auto' | 'playback' | 'transient' | 'transient-solo' | 'ambient' | 'play-and-record';

/**
 *  Sets the audio session type when running in Safari. On other platforms it
 *  does nothing.
 */
export function setSafariAudioSessionType(navigator: Navigator, audioSessionType: SafariAudioSessionType) {
  if (!('audioSession' in navigator)) {
    return;
  }
  if ((typeof navigator.audioSession !== 'object') || (navigator.audioSession === null)) {
    return;
  }

  if (!('type' in navigator.audioSession)) {
    return;
  }

  navigator.audioSession.type = audioSessionType;
}
